import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Platform,
  Image,
  TouchableOpacity,
  Alert,
  ScrollView,
  TouchableWithoutFeedback,
  TextInput,
  KeyboardAvoidingView,
  Keyboard,
  Modal,
  Pressable,
  Animated,
  ActivityIndicator,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import { v2CreateTransfer, v2GetMyBalances, v2GetWithdrawFeeRate, v2GetMinWithdrawAmount, v2GetRates } from '../../../services/v2/api';
import type { V2Balance, V2CreateTransferRequest, V2RateInfo, V2Transfer } from '../../../types/v2';
import { formatUsdtAmount, isUsdPegged } from '../../../utils/usdtEquivalent';
import TabBar from '../../../components/TabBar';
import AmountCaret from '../../../components/UIComponents/AmountCaret';
import SimpleHeader from '../../../components/SimpleHeader';
import TokenIcon from '../../../components/UIComponents/TokenIcon';
import { CARD_SURFACE, COLORS } from '../../../theme/themeTokens';
import { COLORS_WRONG } from '../../../theme/colorsWrong';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useAuth } from '../../../contexts/AuthContext';
import { usePIN } from '../../../contexts/PINContext';
import PINConfirmModal from '../../../components/UIComponents/PINConfirmModal';
import { useSwipeDownDismiss } from '../../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP, WEB_SHEET_MODAL_ANIMATION } from '../../../../web/sheet';
import { compareByNetwork } from '../../../utils/networkOrder';
import { formatAmount, parseAmount, numberToAmountDisplay, formatTokenAmount } from '../../../utils/amountFormat';
import NumericKeypad from '../../../components/NumericKeypad';
import { MAX_AMOUNT_DECIMALS, fitsWithFee, maxAmountWithFee, toRequestAmount } from '../../../utils/moneyAmount';
import { isTonAddress } from '../../../utils/scannedAddress';
import { clearsMinimum, formatMinimumHint, minimumInToken } from '../minimumAmountRule';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';

// Top inset used by the account-picker sheet when rendered inside
// OperationsScreen. Aligns the sheet top just BELOW the screen's
// "From current account" label so the header stays visible above the
// sheet. Breakdown:
//   status bar gutter (20 iOS / 28 Android)
//   + Operations header (44) + 12-dp gap + 44-dp tab strip   = +100
//   + scrollContent.paddingTop (16) + sectionLabel.marginTop (16) = +32
//   + sectionLabel height (~17) + sectionLabel.marginBottom (12) = +29
const OPERATIONS_TOP_INSET = (Platform.OS === 'android' ? 28 : 20) + 161;

// Format balance as "1 234 567,89" — space-separated thousands and comma
// decimal (European convention). Manual formatting (not toLocaleString)
// so output is identical across iOS/Android/Hermes regardless of the
// device's Intl support, and uses a plain space (not NBSP).
const formatBalance = (amount: string): string => {
  const num = parseFloat(amount);
  if (isNaN(num)) { return '0,00'; }
  const [intPart, decPart = '00'] = num.toFixed(2).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped},${decPart}`;
};

// Validate address based on network symbol
const validateAddress = (address: string, networkSymbol: string): boolean => {
  if (!address || address.length === 0) return false;
  
  const symLower = networkSymbol.toLowerCase();
  
  switch (symLower) {
    case 'eth':
    case 'erc20':
      return address.length === 42 && address.startsWith('0x');
    case 'trx':
    case 'trc20':
      return address.length === 34 && address.startsWith('T');
    case 'btc':
      return (
        address.length >= 26 &&
        address.length <= 35 &&
        (address.startsWith('1') || address.startsWith('3') || address.startsWith('bc1'))
      );
    case 'sol':
      return address.length >= 32 && address.length <= 44;
    case 'ton':
      // Any 48 characters passed before — "//transfer/EQ…" from a TON link
      // included. The real forms only: 48 base64url, or workchain:hex.
      return isTonAddress(address);
    case 'xrp':
      return address.startsWith('r') && address.length >= 25 && address.length <= 35;
    default:
      return address.length >= 20;
  }
};

// Get address placeholder based on network symbol
const getAddressPlaceholder = (networkSymbol: string): string => {
  const symLower = networkSymbol.toLowerCase();

  switch (symLower) {
    case 'eth':
    case 'erc20':
      return '42 characters';
    case 'trx':
    case 'trc20':
      return '34 characters';
    case 'btc':
      return '26-35 characters';
    case 'sol':
      return '32-44 characters';
    case 'ton':
      return '48 characters';
    case 'xrp':
      return '25-35 characters';
    default:
      return '42 characters';
  }
};

// Same NBSP-grouped formatter used on the Current Account tile and the
// Inflow picker — keeps numeric output consistent across the screen.
// formatBalanceLikeCard — теперь delegate к formatTokenAmount
// чтобы UI-балансы шли через ту же string-truncation что и Maximum-кнопка.
// Раньше функция использовала toFixed (округление вверх) — это давало
// рассинхрон: balance label показывал "3,06839508" а Maximum давал
// "3,06839507" для того же source-значения. Теперь оба пути truncate.
// Потолок знаков на экране живёт в formatTokenAmount: token_decimals — это
// точность контракта (18 у USDT), а не то, сколько цифр читает человек.
const formatBalanceLikeCard = (amount: number | string, decimals = 2): string =>
  formatTokenAmount(amount, decimals);

/**
 * One row in the flat picker — same shape as on the Inflow tab so the visual
 * matches across Operations. Each (token × network) combination is its own
 * entry; the underlying V2Balance is preserved so address validation,
 * transfer-id and per-network logic keep working downstream.
 */
interface AccountPickerOption {
  key: string;
  symbol: string;
  networkSymbol: string;
  networkName: string;
  tokenName: string;
  iconUrl: string | null;
  amount: number;
  balance: V2Balance;
}

interface WithdrawScreenProps {
  balance: V2Balance;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onTransferSuccess?: () => void;
  onScanQR?: () => void;
  scannedAddress?: string | null;
  onClearScannedAddress?: () => void;
  /** Persisted network ID — survives component remount (e.g. after QR scan) */
  persistedNetworkId?: string | null;
  /** Called when user selects a different network — parent persists the ID */
  onNetworkChange?: (networkId: string) => void;
  /** When true, parent (OperationsScreen) supplies header + tabbar. */
  embedded?: boolean;
  repeatPrefill?: { balanceId?: string; destinationAddress?: string; amount?: string };
}

const WithdrawScreen: React.FC<WithdrawScreenProps> = ({
  balance,
  onBack,
  onNavigate,
  onTransferSuccess,
  onScanQR,
  embedded = false,
  scannedAddress,
  onClearScannedAddress,
  persistedNetworkId,
  onNetworkChange,
  repeatPrefill,
}) => {
  // Flat (token × network) picker — same shape as the Inflow tab. Selection
  // here is the source-of-truth for currency / network / address validation
  // downstream; `selectedNetwork` reflects the picked option's balance so the
  // existing transfer code (matchedBalanceId, networkSymbol, …) keeps working.
  const [pickerOptions, setPickerOptions] = useState<AccountPickerOption[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<V2Balance | null>(null);
  const [networkModalVisible, setNetworkModalVisible] = useState(false);
  // Swipe-to-dismiss for the Current-accounts picker — same gesture
  // behaviour as the NumericKeypad and the Inflow picker.
  const networkSwipe = useSwipeDownDismiss(
    networkModalVisible,
    () => setNetworkModalVisible(false),
  );
  const [loadingNetworks, setLoadingNetworks] = useState(true);

  // Active network balance (selected or fallback)
  const activeNetwork = selectedNetwork || balance;
  const currency = activeNetwork.token_symbol || balance.token_symbol || 'USDT';
  const networkSymbol = activeNetwork.network_symbol || '';
  const networkName = activeNetwork.network_name || '';

  // Matched balance for this token+account (used for transfer and available amount display)
  const [matchedAmount, setMatchedAmount] = useState<string>(balance.available_amount ?? balance.amount);
  const [matchedBalanceId, setMatchedBalanceId] = useState<string>(balance.id);

  // State
  const [walletAddress, setWalletAddress] = useState('');
  // transferAmount is the display string entered into the custom
  // NumericKeypad. Format is "1 234,56" — strip via parseAmount() before
  // arithmetic; mirror DepositCardScreen.
  const [transferAmount, setTransferAmount] = useState('');
  const [keypadVisible, setKeypadVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);

  // Success modal — shown after v2CreateTransfer resolves. The submitted
  // amount + currency are snapshotted at submit time so the modal stays
  // stable even if the user changes the input afterwards.
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState<number>(0);
  const [submittedCurrency, setSubmittedCurrency] = useState<string>('');
  // The created withdrawal itself, for the receipt: its own figures, its
  // date and the request number the client quotes to support.
  const [createdTransfer, setCreatedTransfer] = useState<V2Transfer | null>(null);

  // Live withdraw-fee rate from v1_2_fee_settings ("withdraw" code), shown
  // as "Fee, X%" on the form. `null` until the fetch completes; the row is
  // hidden in that brief window so it doesn't flash a stale value.
  const [feeRate, setFeeRate] = useState<number | null>(null);
  // The same rate as the backend's decimal string: Max and the balance check
  // work on it exactly, as the server does.
  const [feeRateText, setFeeRateText] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetWithdrawFeeRate();
        const n = parseFloat(res.percent);
        if (mounted && Number.isFinite(n) && n >= 0) {
          setFeeRate(n);
          setFeeRateText(res.percent);
        }
      } catch {
        // Silent — Fee row stays hidden if the request fails.
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Live "Minimum amount" threshold from v1_2_amount_settings
  // ("min_amount_withdraw"). `null` until the fetch lands. Used for both
  // the info tile AND the amount-validation gate on the Confirm button.
  // The threshold is denominated in USDT and converted into the withdrawn
  // token once, in `minAmountInCurrency`, so the hint and the gate read the
  // same figure (see minimumAmountRule).
  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [rates, setRates] = useState<Record<string, V2RateInfo>>({});
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await v2GetMinWithdrawAmount();
        const n = parseFloat(res.value);
        if (mounted && Number.isFinite(n) && n >= 0) {
          setMinAmount(n);
        }
      } catch {
        // Silent — Minimum amount row stays hidden if the request fails.
      }
    })();
    (async () => {
      try {
        const res = await v2GetRates();
        if (mounted) {
          setRates(res.rates ?? {});
        }
      } catch {
        // Silent — minimum validation falls back to fail-open when rates miss.
      }
    })();
    return () => { mounted = false; };
  }, []);

  // PIN-confirm состояние. Если PIN не настроен (или юзер пропустил setup) —
  // submit без модалки, как раньше.
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per withdrawal: a press after a lost answer resends it with
  // the same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<V2CreateTransferRequest>();

  // Fetch all balances and build flat (token × network) picker options across
  // every current-account, non-virtual row. Same model the Inflow tab uses.
  const prefilledRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const fetchPickerOptions = async () => {
      try {
        setLoadingNetworks(true);
        const res = await v2GetMyBalances();
        if (cancelled) { return; }

        const allBalances = res.balances || [];

        const currentReal = allBalances.filter(
          (b) =>
            (b.account_type_code ?? 'current') === 'current' &&
            b.provider_name !== 'virtual',
        );

        const opts: AccountPickerOption[] = currentReal.map((b) => ({
          key: `${b.token_symbol}|${b.network_symbol || 'no-net'}|${b.wallet_address || b.wallet_id || b.id}`,
          symbol: b.token_symbol,
          networkSymbol: b.network_symbol || '',
          networkName: b.network_name || '',
          tokenName: b.token_name || b.token_symbol,
          iconUrl: b.icon_url ?? null,
          // Use available_amount (amount − locked_amount) so the picker
          // value matches the form's Available row; raw `amount` would
          // double-count pending withdraw locks.
          amount: parseFloat(b.available_amount ?? b.amount ?? '0') || 0,
          balance: b,
        }));

        // Network-rank first (TRON → Ethereum → others), then within each
        // network USDT comes before everything else, and alphabetical for
        // the rest. Mirrors the Top-up tab and matches the product
        // expectation "Ethereum row: USDT first, then the others".
        const tokenRank = (s: string) => (s.toUpperCase() === 'USDT' ? 0 : 1);
        opts.sort((a, b) => {
          const byNetwork = compareByNetwork(a, b);
          if (byNetwork !== 0) return byNetwork;
          const ta = tokenRank(a.symbol);
          const tb = tokenRank(b.symbol);
          if (ta !== tb) return ta - tb;
          return a.symbol.localeCompare(b.symbol);
        });

        if (opts.length === 0) {
          // Fallback to the prop so the screen still renders something usable.
          setPickerOptions([]);
          setSelectedNetwork(balance);
          setMatchedAmount(balance.available_amount ?? balance.amount);
          setMatchedBalanceId(balance.id);
          return;
        }

        setPickerOptions(opts);

        // Pre-select: persisted network wins (survives QR-scan remount so the
        // user's mid-flow choice isn't blown away). Otherwise default to the
        // first sorted option — TRON-first → USDT first → so this is the
        // USDT/TRON row on a fresh open. The `balance` prop is no longer a
        // fallback because callers (Dashboard / BalanceDetail) often pass
        // whichever balance they had handy, which would silently override
        // the intended default.
        const preselected =
          (repeatPrefill?.balanceId && opts.find((o) => o.balance.id === repeatPrefill.balanceId))
          || (persistedNetworkId && opts.find((o) => o.balance.id === persistedNetworkId))
          || opts[0];
        setSelectedNetwork(preselected.balance);
        onNetworkChange?.(preselected.balance.id);

        // Matched amount/id for transfer — bound to the picked balance so
        // Withdraw drains the same row the user picked.
        setMatchedAmount(preselected.balance.available_amount ?? preselected.balance.amount);
        setMatchedBalanceId(preselected.balance.id);
        // Prefilled once, when the screen opens on a repeated order. A later
        // reload must not overwrite what the client has typed since.
        if (!prefilledRef.current) {
          prefilledRef.current = true;
          if (repeatPrefill?.destinationAddress) {
            setWalletAddress(repeatPrefill.destinationAddress);
          }
          if (repeatPrefill?.amount) {
            setTransferAmount(numberToAmountDisplay(
              repeatPrefill.amount,
              Math.min(preselected.balance.token_decimals ?? 2, MAX_AMOUNT_DECIMALS),
            ));
          }
        }
      } catch (err) {
        console.warn('[Withdraw] Failed to load picker options:', err);
        setPickerOptions([]);
        setSelectedNetwork(balance);
      } finally {
        if (!cancelled) { setLoadingNetworks(false); }
      }
    };

    fetchPickerOptions();
    return () => { cancelled = true; };
    // The parent rebuilds `balance` on every render of its own, so the
    // list reloads on the id instead — a reload used to overwrite what the
    // client had already typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance.id, persistedNetworkId, repeatPrefill?.amount, repeatPrefill?.balanceId, repeatPrefill?.destinationAddress]);

  // Handle scanned address from QR scanner
  useEffect(() => {
    if (scannedAddress) {
      setWalletAddress(scannedAddress);
      if (onClearScannedAddress) {
        onClearScannedAddress();
      }
    }
  }, [scannedAddress, onClearScannedAddress]);

  const handleSelectOption = (opt: AccountPickerOption) => {
    setSelectedNetwork(opt.balance);
    setNetworkModalVisible(false);
    onNetworkChange?.(opt.balance.id);
    setMatchedAmount(opt.balance.available_amount ?? opt.balance.amount);
    setMatchedBalanceId(opt.balance.id);
  };

  const handlePasteAddress = async () => {
    try {
      const text = await Clipboard.getString();
      if (text) {
        setWalletAddress(text.trim());
      }
    } catch (err) {
      console.warn('[Withdraw] Clipboard read failed:', err);
    }
  };

  // Derived display strings for the picker card / modal — same layout as Inflow.
  const activePickerOption = pickerOptions.find((o) => o.balance.id === activeNetwork.id) ?? null;
  const pickerTitle = activePickerOption
    ? activePickerOption.networkSymbol
      ? `${activePickerOption.symbol} - ${activePickerOption.networkSymbol}`
      : activePickerOption.symbol
    : `${currency}${networkSymbol ? ` - ${networkSymbol}` : ''}`;
  const pickerSubtitle = activePickerOption?.networkName || networkName || '';

  // Receipt helpers — the address is too long to print whole, and the date
  // reads as it does on the Exchange receipt.
  const shortAddress = (value: string | null | undefined): string => {
    const v = (value ?? '').trim();
    if (v.length <= 20) { return v || '—'; }
    return `${v.slice(0, 10)}…${v.slice(-6)}`;
  };
  const formatTransferDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) { return iso; }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // Transfer amount — display string "1 234,56" → number 1234.56.
  const amount = parseAmount(transferAmount);

  // Available balance = matched balance for this token + account
  const availableBalance = parseFloat(matchedAmount) || 0;

  // Fee on top of the entered amount. Stored fraction (0.02 = 2%) is
  // multiplied by the amount; displayed as a separate "Fee, X%" row.
  // Backend doesn't deduct it yet — visual-only — but we still include it
  // in the available-balance check so the user can't queue a withdrawal
  // that would overflow once the deduction goes live.
  const feeValue = feeRate != null ? amount * feeRate : 0;
  const amountWithFee = amount + feeValue;

  // Validation. Minimum-amount gate is applied only once the live threshold
  // has loaded — until then `minAmount === null` skips the lower-bound check.
  // Min is in USDT: convert the entered amount via live rates and compare.
  // Unknown rate → fail-open (don't block when we can't price).
  const isValidAddress = validateAddress(walletAddress, networkSymbol);
  const isAddressError = walletAddress.length > 0 && !isValidAddress;

  // Пересчёт "Minimum amount" из USDT-threshold'а в валюту, выбранную в
  // "From current account". Backend хранит min в USDT (v1_2_amount_settings
  // "min_amount_withdraw"), но юзеру в BTC/ETH/… context'е USDT-значение
  // трудно интерпретировать. Fallback на raw USDT когда rate неизвестен.
  //
  // Rounded UP to the token's precision, and the hint and the check now read
  // the same number: cutting the quotient downward put a figure on screen
  // worth a hair less than the threshold, and the form refused it. The
  // Exchange screen had the same fault, visibly, because it also prefilled
  // the field with it.
  const amountDecimals = activeNetwork?.token_decimals ?? 2;
  const minAmountInCurrency: number | null = (() => {
    if (isUsdPegged(currency)) { return minimumInToken(minAmount, 1, amountDecimals); }
    const info = rates[currency.toUpperCase()] ?? rates[currency];
    return minimumInToken(minAmount, parseFloat(info?.price_usd ?? ''), amountDecimals);
  })();

  // The withdrawal fee is charged on top, so the most that can leave the
  // account is the balance less that fee — that, not the balance, is
  // "everything" for the dust rule. A balance under the minimum can be
  // emptied; anything less than all of it still cannot.
  const maxSpendable = feeRate != null && feeRate > 0
    ? availableBalance / (1 + feeRate)
    : availableBalance;
  const meetsMinimum = clearsMinimum({
    amount,
    minInToken: minAmountInCurrency,
    availableBalance,
    maxSpendable,
    decimals: amountDecimals,
  });
  const withdrawingDust =
    minAmountInCurrency != null && availableBalance > 0 && availableBalance < minAmountInCurrency;

  // The amount as it will be sent, and whether it fits the balance with the
  // fee on top — in exact decimals, the way the server checks it. A float sum
  // could refuse the very figure Max had put there.
  const requestAmount = toRequestAmount(transferAmount);
  const fitsBalance = feeRateText != null
    ? fitsWithFee(requestAmount, matchedAmount, feeRateText)
    : fitsWithFee(requestAmount, matchedAmount, '0');
  const isValidAmount = amount > 0 && fitsBalance && meetsMinimum;
  // can_withdraw is forced to false when the session is an impersonation session.
  // Backend will 403 anyway; this just disables the button so the super-user
  // doesn't waste time filling out a form that would fail.
  const caps = useCapabilities();
  // Explicit impersonation-guard в дополнение к caps.can_withdraw: под
  // impersonation backend hard-deny'ит POST /transfers (403). caps должен уже
  // быть false, guard-net на случай stale auth-state.
  const { isImpersonating } = useAuth();
  const canTransfer = isValidAddress && isValidAmount && !loadingNetworks && caps.can_withdraw && !isImpersonating;

  // Defensive: same floor logic as DepositCardScreen. parseFloat-of-string
  // can yield a value whose .toFixed(2) rounds UP past the real balance
  // (FP imprecision); when piped through the keypad's parser it would
  // trip the over-balance gate. Compare in cents already handles equality,
  // but keep the display string honest by reformatting via the shared
  // helper.
  const handleMaximum = () => {
    // STRING-source (минуя parseFloat) для full precision — см.
    // numberToAmountDisplay docstring про IEEE 754 артефакты на 3.06839508.
    // The fee is charged on top, so "everything" is the most that leaves the
    // balance together with its fee — not the balance, which Confirm would
    // then refuse. Cut to the token's places, eight at most, as the keypad.
    const decimals = Math.min(activeNetwork?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS);
    const balanceStr = activeNetwork?.available_amount ?? activeNetwork?.amount ?? '0';
    const most = feeRateText != null ? maxAmountWithFee(balanceStr, feeRateText, decimals) : balanceStr;
    setTransferAmount(numberToAmountDisplay(most, decimals));
  };

  // Старая Alert.alert('Confirm Transfer', ...) удалена в пользу
  // PIN-confirm модалки. Если PIN не настроен — submit идёт сразу.
  const handleTransfer = () => {
    if (!canTransfer || isSubmitting) return;
    if (!isPINSetup || isPINSkipped) {
      executeTransfer();
    } else {
      setPinModalVisible(true);
    }
  };

  const executeTransfer = async () => {
    setPinModalVisible(false);
    setIsSubmitting(true);

    // Use the matched balance_id (token_symbol + account_id)
    // Network info comes from the selected network (Fireblocks wallet)
    const request: V2CreateTransferRequest = {
      balance_id: matchedBalanceId,
      destination_address: walletAddress,
      amount: requestAmount,
      network: activeNetwork.network_name,
      // The picked balance's account and wallet, like balance_id and network
      // above — not the `balance` prop's: the screen opens on the first option,
      // whichever balance it was opened from.
      account_id: activeNetwork.account_id || undefined,
      wallet_id: activeNetwork.wallet_id || undefined,
    };

    console.log('Creating v2 transfer:', JSON.stringify(request));

    try {
      const attempt = idempotency.attempt(request);
      const transfer = await v2CreateTransfer(attempt.request, attempt.key);
      idempotency.answered();
      console.log('Transfer created:', transfer.id, 'Status:', transfer.status);

      // Snapshot the submitted figures for the success modal — the user
      // could clear the input before tapping OK and we'd lose them.
      // Используем amountWithFee (= amount + fee) — backend now locks +
      // debits amount+fee (см. CreateTransfer LockAmount), поэтому
      // итоговая сумма списания должна показываться целиком, не голый
      // amount.
      setSubmittedAmount(amountWithFee);
      setSubmittedCurrency(currency);
      setCreatedTransfer(transfer);
      setSuccessModalVisible(true);
    } catch (error) {
      idempotency.failed(error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      console.error('Transfer failed:', errorMessage);
      Alert.alert('Transfer Failed', errorMessage, [{ text: 'OK' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScanQR = () => {
    if (onScanQR) {
      onScanQR();
    }
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      {!embedded && (
        <SimpleHeader
          title="Withdraw"
          onBack={onBack}
          backgroundVariant="primaryNavy"
        />
      )}

      {/* TouchableWithoutFeedback + KeyboardAvoidingView wrappers removed —
          they were the only structural difference vs Inflow / Top-up
          (those screens have a plain <ScrollView> directly under the
          container). Tap-to-dismiss the keyboard is still handled via
          ScrollView's keyboardShouldPersistTaps='handled'. */}
          <ScrollView
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* From current account — same picker as the Inflow tab. Tap
                opens a bottom-sheet listing all (token × network) combos. */}
            <Text style={styles.sectionLabel}>From current account</Text>
            {loadingNetworks ? (
              <View style={[styles.networkSelector, styles.networkSelectorCenter]}>
                <ActivityIndicator size="small" color={COLORS.brandOrange} />
              </View>
            ) : (
              <TouchableOpacity
                style={styles.networkSelector}
                onPress={() => setNetworkModalVisible(true)}
                activeOpacity={0.7}
              >
                <TokenIcon
                  symbol={currency}
                  iconUrl={activeNetwork.icon_url}
                  style={styles.networkSelectorIcon}/>
                <View style={styles.networkTextContainer}>
                  <Text style={styles.networkName}>
                    {pickerTitle}
                  </Text>
                  {pickerSubtitle ? (
                    <Text style={styles.networkSubtitle}>
                      {pickerSubtitle}
                    </Text>
                  ) : null}
                </View>
                <Text style={styles.pickerAmount}>
                  {formatBalanceLikeCard(parseFloat(matchedAmount) || 0, activeNetwork.token_decimals)} {currency}
                </Text>
                <Image
                  source={require('../../../assets/arrow_left_20.png')}
                  style={styles.chevronIcon}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            )}

            {/* Wallet Address Section — QR scan icon next to the header,
                clipboard-paste icon inside the input on the right. */}
            <View style={styles.addressLabelRow}>
              <Text style={styles.addressLabelText}>Wallet address</Text>
              <TouchableOpacity
                onPress={handleScanQR}
                style={styles.scanButton}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Image
                  source={require('../../../assets/qr_code_small.png')}
                  style={styles.qrLabelIcon}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>
            <View style={[styles.inputContainer, isAddressError && styles.inputError]}>
              <TextInput
                style={styles.textInput}
                placeholder="42 characters"
                placeholderTextColor={COLORS.blueSteel}
                value={walletAddress}
                onChangeText={setWalletAddress}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <TouchableOpacity
                onPress={handlePasteAddress}
                style={styles.scanButton}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Image
                  source={require('../../../assets/sign_in.png')}
                  style={styles.pasteIcon}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>

            {/* Transfer amount input — same custom keypad as the Top-up tab
                on DepositCardScreen. Tapping opens NumericKeypad with the
                available balance + Max / Clean controls. */}
            <TouchableOpacity
              style={[styles.inputContainer, styles.amountContainerSpacing]}
              activeOpacity={0.7}
              onPress={() => !isSubmitting && setKeypadVisible(true)}
              disabled={isSubmitting}
            >
              <Text style={styles.amountLabel}>Enter amount</Text>
              <Text style={[styles.textInput, styles.amountInput]}>
                {transferAmount || ''}
              </Text>
              <AmountCaret active={keypadVisible} />
              {transferAmount.length > 0 ? (
                <Text style={styles.amountSuffix}>{currency}</Text>
              ) : null}
            </TouchableOpacity>

            {/* Combined info tile: Fee row + Minimum amount row stacked
                inside a single card. Rendered when at least one of the two
                values has loaded; a thin divider separates them when both
                are present. The Confirm button stays disabled while the
                entered amount is below the minimum or exceeds available. */}
            {(feeRate != null || minAmount != null) ? (
              <View style={[styles.infoCard, styles.feeRowSpacing, styles.infoCardFlat]}>
                {feeRate != null ? (
                  <View style={styles.infoCardRow}>
                    <Text style={styles.amountLabel}>
                      Fee, {(feeRate * 100).toFixed(2).replace(/\.?0+$/, '')}%
                    </Text>
                    <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                      {feeValue > 0
                        ? `${formatBalanceLikeCard(feeValue, activeNetwork.token_decimals)} ${currency}`
                        : `0 ${currency}`}
                    </Text>
                  </View>
                ) : null}

                {feeRate != null && minAmount != null ? (
                  <View style={styles.infoCardDivider} />
                ) : null}

                {minAmount != null ? (
                  <View style={styles.infoCardRow}>
                    <Text style={styles.amountLabel}>Minimum amount</Text>
                    <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                      {minAmountInCurrency != null
                        ? `${formatMinimumHint(minAmountInCurrency, activeNetwork.token_decimals)} ${currency}`
                        : formatUsdtAmount(minAmount, 0)}
                    </Text>
                  </View>
                ) : null}

                {/* Total amount — амаунт + комиссия. Divider всегда нужен
                    (если info card вообще рендерится, то выше есть хотя бы
                    Fee или Minimum). Зеркало "Total" в OneAdmin Withdrawals
                    modal'е. */}
                <View style={styles.infoCardDivider} />
                <View style={styles.infoCardRow}>
                  <Text style={styles.amountLabel}>Total amount</Text>
                  <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                    {`${formatBalanceLikeCard(amountWithFee, activeNetwork.token_decimals)} ${currency}`}
                  </Text>
                </View>
              </View>
            ) : null}

            {/* Why Confirm is dead. The gate was silent, so a client who
                held less than the minimum had no way to learn that the
                whole balance is what unlocks it. */}
            {amount > 0 && !meetsMinimum ? (
              <Text style={styles.minimumNote}>
                {withdrawingDust
                  ? `Below the minimum. Your balance is under it too — withdraw all ${formatMinimumHint(maxSpendable, amountDecimals)} ${currency} to empty the account.`
                  : 'Below the minimum for a withdrawal.'}
              </Text>
            ) : null}

            {/* Expected-processing info frame — sits above the Confirm CTA.
                Clock icon on the left, message in Secondary/Steel Navy with
                the duration emphasis using a heavier weight. */}
            <View style={styles.expectedFrame}>
              <Image
                source={require('../../../assets/clock.png')}
                style={styles.expectedIcon}
                resizeMode="contain"
              />
              <Text style={styles.expectedText}>
                Expected processing: <Text style={styles.expectedStrong}>up to 24 hours</Text> (working days)
              </Text>
            </View>

            {/* Confirm transfer button */}
            <TouchableOpacity
            style={[styles.transferButton, (!canTransfer || isSubmitting) && styles.transferButtonDisabled]}
            onPress={handleTransfer}
            activeOpacity={canTransfer && !isSubmitting ? 0.8 : 1}
            disabled={!canTransfer || isSubmitting}
          >
            <Text style={[styles.transferButtonText, (!canTransfer || isSubmitting) && styles.transferButtonTextDisabled]}>
              {isSubmitting ? 'Processing...' : 'Confirm transfer'}
            </Text>
            </TouchableOpacity>

            {/* Bottom spacing for scroll */}
            <View style={{ height: 40 }} />
          </ScrollView>

      {/* Network selection bottom-sheet modal — slide-up animation
          matches DepositCardScreen (Top-up) so all three Operations
          tabs share the same picker feel. */}
      <Modal
        visible={networkModalVisible}
        transparent
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'slide'}
        statusBarTranslucent
        onRequestClose={() => setNetworkModalVisible(false)}
      >
        <Pressable
          style={[styles.modalOverlay, WEB_SHEET_BACKDROP]}
          onPress={() => setNetworkModalVisible(false)}
        >
          <Animated.View
            style={[
              styles.modalContent,
              embedded && { marginTop: OPERATIONS_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: networkSwipe.translateY }] },
            ]}
            {...networkSwipe.panHandlers}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <View style={styles.modalHandle} />
              <View style={styles.modalTitleRow}>
                <Text style={styles.modalTitle}>Current accounts</Text>
                <Text style={styles.modalTitleAside}>Available balance</Text>
              </View>
            {pickerOptions.length === 0 ? (
              <Text style={[styles.modalOptionSubtitle, { paddingHorizontal: 12 }]}>
                No current-account balances available.
              </Text>
            ) : null}
            {pickerOptions.map((opt) => {
              const isSelected = selectedNetwork?.id === opt.balance.id;
              const title = opt.networkSymbol
                ? `${opt.symbol} - ${opt.networkSymbol}`
                : opt.symbol;
              return (
                <TouchableOpacity
                  key={opt.key}
                  style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                  onPress={() => handleSelectOption(opt)}
                  activeOpacity={0.7}
                >
                  <TokenIcon
                    symbol={opt.symbol}
                    iconUrl={opt.iconUrl}
                    style={styles.modalOptionIcon}/>
                  <View style={styles.modalOptionTextContainer}>
                    <Text style={styles.modalOptionName}>{title}</Text>
                    {opt.networkName ? (
                      <Text style={styles.modalOptionSubtitle}>{opt.networkName}</Text>
                    ) : null}
                  </View>
                  <Text style={styles.modalOptionAmount}>
                    {formatBalanceLikeCard(opt.amount, opt.balance.token_decimals)} {opt.symbol}
                  </Text>
                </TouchableOpacity>
              );
            })}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>

      {!embedded && <TabBar activeTab="dashboard" onNavigate={onNavigate} />}

      <PINConfirmModal
        visible={pinModalVisible}
        title="Confirm withdrawal"
        subtitle={`${formatBalance(String(amount))} ${currency} → ${walletAddress.slice(0, 10)}…${walletAddress.slice(-6)}`}
        onSuccess={executeTransfer}
        onCancel={() => setPinModalVisible(false)}
      />

      {/* Custom numeric keypad — same component as DepositCardScreen. The
          onChange callback runs through formatAmount so the display stays
          in canonical "1 234,56" form; Max fills with the available
          balance via numberToAmountDisplay; Clean clears the input. */}
      <NumericKeypad
        visible={keypadVisible}
        value={transferAmount}
        // The token's own precision, not two places. The default cut every
        // entry to cents, so a BTC withdrawal could not be typed at all
        // below 0,01 BTC — and the minimum, 0,00059641 BTC, was literally
        // unenterable. Maximum worked only because it writes the state
        // directly. Capped at eight, the precision the amount comparisons
        // use.
        onChange={(next) => setTransferAmount(
          formatAmount(next, Math.min(activeNetwork?.token_decimals ?? 2, 8)),
        )}
        onClose={() => setKeypadVisible(false)}
        availableValue={`${formatBalanceLikeCard(
          activeNetwork?.available_amount ?? activeNetwork?.amount ?? '0',
          activeNetwork.token_decimals,
        )} ${currency}`}
        onMax={handleMaximum}
        onClean={() => setTransferAmount('')}
        transparentOverlay
      />

      {/* Success modal — replaces the legacy TransferSuccessScreen flow.
          Snapshots the submitted amount + currency so the displayed figures
          don't change if the user edits the form before tapping OK. */}
      <Modal
        visible={successModalVisible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => {
          // OK closes the modal and returns to the screen that opened
          // Operations (Dashboard / BalanceDetail). The legacy
          // TransferSuccessScreen ("Back to dashboard") is skipped.
          setSuccessModalVisible(false);
          onBack();
        }}
      >
        <View style={styles.successOverlay}>
          <View style={styles.successCard}>
            <Image
              source={require('../../../assets/wallet2.png')}
              style={styles.successIcon}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>Request sent</Text>
            <Text style={styles.successSubtitle}>
              Operation is being processed.
            </Text>
            <View style={styles.successAmountRow}>
              <Text style={styles.successAmount}>
                -{formatBalance(String(submittedAmount))}
              </Text>
              <Text style={styles.successCurrency}> {submittedCurrency}</Text>
            </View>

            {/* The same receipt the Exchange screen prints: what was asked
                for, in the figures the server recorded. */}
            <View style={styles.successDetails}>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Network</Text>
                <Text style={styles.successDetailValue}>
                  {createdTransfer?.network || activeNetwork?.network_name || '—'}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>To</Text>
                <Text style={styles.successDetailValue}>
                  {shortAddress(createdTransfer?.destination_address ?? walletAddress)}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Amount</Text>
                <Text style={styles.successDetailValue}>
                  {formatBalance(createdTransfer?.amount ?? String(amount))} {submittedCurrency}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Fee</Text>
                <Text style={styles.successDetailValue}>
                  {formatBalance(createdTransfer?.fee ?? String(feeValue))} {submittedCurrency}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Total</Text>
                <Text style={styles.successDetailValue}>
                  {formatBalance(String(submittedAmount))} {submittedCurrency}
                </Text>
              </View>
              <View style={styles.successDetailRow}>
                <Text style={styles.successDetailLabel}>Date</Text>
                <Text style={styles.successDetailValue}>
                  {createdTransfer ? formatTransferDate(createdTransfer.created_at) : ''}
                </Text>
              </View>
              {createdTransfer?.number ? (
                <View style={styles.successDetailRow}>
                  <Text style={styles.successDetailLabel}>Request ID</Text>
                  <Text style={styles.successDetailValue}>{createdTransfer.number}</Text>
                </View>
              ) : null}
            </View>

            <TouchableOpacity
              style={styles.successButton}
              onPress={() => {
                setSuccessModalVisible(false);
                onBack();
              }}
              activeOpacity={0.8}
            >
              <Text style={styles.successButtonText}>OK</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.successSecondaryButton}
              onPress={() => {
                setSuccessModalVisible(false);
                onNavigate('orders');
              }}
              activeOpacity={0.8}
            >
              <Text style={styles.successSecondaryText}>View request status</Text>
              <Image
                source={require('../../../assets/upper-right-arrow.png')}
                style={styles.successSecondaryIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  successDetails: { alignSelf: 'stretch', marginTop: 20 },
  successDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  successDetailLabel: { fontFamily: 'Inter', fontSize: 14, color: COLORS.blueSteel },
  successDetailValue: { fontFamily: 'Inter', fontSize: 14, color: COLORS.primaryNavy },
  successSecondaryButton: {
    alignSelf: 'stretch',
    marginTop: 8,
    height: 48,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
  },
  successSecondaryText: { fontFamily: 'Inter', fontSize: 16, fontWeight: '500', color: COLORS.primaryNavy },
  // An icon, not the ↗ character: iOS draws that one as a blue emoji tile.
  successSecondaryIcon: { width: 16, height: 16, marginLeft: 6, tintColor: COLORS.primaryNavy },
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  contentContainer: {
    paddingTop: 16,
    // 16 dp left/right matches DepositCardScreen (Top-up). Was 20.
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  sectionLabel: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
    // 16 / 8 mirror ConfirmDepositScreen.sectionLabel — keeps the
    // distance from "From current account" label to the picker tile
    // identical on Inflow ↔ Withdraw so switching tabs doesn't shift
    // the tile by 4 dp.
    marginTop: 16,
    marginBottom: 8,
  },
  // Source Account Card
  sourceAccountCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    ...CARD_SURFACE,
    marginBottom: 24,
  },
  sourceAccountLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sourceAccountText: {
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 18,
    color: COLORS.textPrimary,
  },
  sourceAccountRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sourceAccountBalance: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 18,
    color: COLORS.textPrimary,
  },
  // Collapsed "From current account" — mirrors AccountCard sizing on
  // AllAssets (66 DP tall, white tile with subtle border).
  networkSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    ...CARD_SURFACE,
    // 8 dp matches DepositCardScreen's pickerTile and the inputContainer
    // below — keeps the gap between tiles consistent across screens.
    marginBottom: 8,
  },
  networkSelectorIcon: {
    // 28 dp matches DepositCardScreen.tokenIcon — same icon size in the
    // "From current account" picker tile across Withdraw and Top-up.
    width: 28,
    height: 28,
    // 12 dp matches ConfirmDepositScreen.currencyIcon — same icon-text
    // gap on Inflow ↔ Withdraw.
    marginRight: 12,
  },
  networkTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  networkName: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  networkSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  chevronIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },
  /* From-current-account picker — amount in the right column, same
     typography as AccountCard's `value` style. */
  pickerAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  /* Header row above the wallet address input. The QR-scan icon sits next
     to the label, while the paste icon lives inside the input on the right. */
  addressLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 0,
    marginBottom: 8,
  },
  /* "Wallet address" label — same typography as DepositCardScreen
     .fieldLabel ("To card") so the gap between the surrounding tiles
     (networkSelector → address tile) reads identically to Top-up's
     gap between From-pickerTile → To-pickerTile around the "To card"
     label. lineHeight: 22 → label area height = 22 dp, matching the
     margins on both sides for parity with Top-up. */
  addressLabelText: {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  qrLabelIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
    tintColor: COLORS.blueSteel,
  },
  pasteIcon: {
    width: 22,
    height: 22,
    tintColor: '#E53935',
  },
  /* Modal title row (left heading + right aside). */
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitleAside: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  /* Amount on the right side of each modal option — matches AccountCard's
     `value` typography on AllAssets. */
  modalOptionAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  selectorRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  selectorLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  currencyIcon: {
    width: 36,
    height: 36,
    marginRight: 12,
  },
  currencyName: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 18,
    lineHeight: 19,
    color: COLORS.steelNavy,
  },
  networkBadge: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 10,
    backgroundColor: COLORS.brandOrange,
    borderRadius: 4,
  },
  networkBadgeText: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 17,
    color: COLORS.white,
  },
  chevron: {
    fontSize: 18,
    color: COLORS.steelNavy,
  },
  // Input Fields
  /* Address + amount input wrappers — same AccountCard chrome as the
     picker tiles above (white tile with 1px muted border). */
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    ...CARD_SURFACE,
    // 16 dp uniform gap to the next element (amount tile / info card),
    // per the "16 dp between elements without label" rule.
    marginBottom: 16,
  },
  inputError: {
    borderColor: COLORS.brandOrange,
  },
  amountContainerSpacing: {
    // No-op now — inputContainer.marginBottom (16) above owns the gap.
    marginTop: 0,
  },
  /* Spacing before the Fee row — gap owned by the amount tile above
     (inputContainer.marginBottom: 16). This tile keeps its own
     marginBottom: 16 so the gap to expectedFrame is uniform. */
  feeRowSpacing: {
    marginTop: 0,
    marginBottom: 16,
  },
  feeAmount: {
    flex: 1,
    color: COLORS.blueSteel,
  },
  /* Combined info tile (Fee + Minimum amount) — same AccountCard chrome as
     the picker / inputs above but with stacked rows. */
  infoCard: {
    ...CARD_SURFACE,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  /* "Flat" variant — used for the Fee / Minimum amount block so it
     blends with the pureLight screen background instead of standing
     up as a white tile. Keeps the muted border outline + divider. */
  infoCardFlat: {
    backgroundColor: COLORS.pureLight,
  },
  /* Regular-weight override for numbers in the flat info card — base
     `amountInput` is 16/500 (medium); `feeAmountRegular` brings it down
     to 16/400 so values read as informational text rather than input. */
  feeAmountRegular: {
    fontWeight: '400',
  },
  infoCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
  },
  /* Divider between rows — aligned with the text content above and below
     (no extra inset, inherits the card's 14 dp horizontal padding). */
  infoCardDivider: {
    height: 1,
    backgroundColor: COLORS.borderMuted,
  },
  /* Expected-processing info frame above the Confirm button. Steel-navy
     border + steel-navy text. The duration emphasis uses a heavier weight. */
  minimumNote: {
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
    color: COLORS.brandOrange,
    marginTop: 12,
  },
  expectedFrame: {
    ...CARD_SURFACE,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 16,
  },
  expectedIcon: {
    width: 16,
    height: 16,
    marginRight: 10,
    tintColor: COLORS.steelNavy,
  },
  expectedText: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 14,
    color: COLORS.steelNavy,
    textAlign: 'left',
  },
  expectedStrong: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 14,
    color: COLORS.steelNavy,
  },
  amountLabel: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  amountInput: {
    textAlign: 'right',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  amountSuffix: {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.steelNavy,
  },
  /* Blinking caret next to the amount input — 2-dp wide vertical line
     matching the amount text baseline. Visibility flips on a 500 ms
     tick via the local <BlinkingCursor> component. Mirror of
     DepositCardScreen / OpenDepositScreen. */
  textInput: {
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '400',
    // 16 dp matches amountLabel ("Enter amount") so the placeholder
    // ("42 characters") and the typed wallet address line up with the
    // sibling field below.
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.steelNavy,
    padding: 0,
  },
  scanButton: {
    // padding: 1 keeps the scanButton (icon 20×20 + padding) within the
    // 22-dp height of addressLabelRow — otherwise the row stretches to
    // 28 dp and the address-input tile ends up 6 dp lower than the
    // equivalent To-tile on Top-up. hitSlop on the TouchableOpacity
    // still provides a generous tap target.
    padding: 1,
  },
  scanIcon: {
    width: 21,
    height: 21,
  },
  warningText: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 17,
    color: COLORS.blueSteel,
    marginBottom: 24,
  },
  // Amount Info Row
  amountInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  maximumLink: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.brandOrange,
    textDecorationLine: 'underline',
  },
  // Total Card
  totalCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    backgroundColor: COLORS.white,
    shadowColor: COLORS.softShadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 2.8,
    elevation: 3,
    borderRadius: 12,
    marginBottom: 24,
  },
  totalLabel: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 18,
    lineHeight: 19,
    color: COLORS.steelNavy,
  },
  totalRightColumn: {
    alignItems: 'flex-end',
  },
  totalValue: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 18,
    lineHeight: 19,
    color: COLORS.steelNavy,
  },
  // Transfer Button
  transferButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    gap: 12,
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  transferButtonDisabled: {
    backgroundColor: COLORS_WRONG.disabled,
  },
  transferButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  transferButtonTextDisabled: {
    color: COLORS.white,
  },
  // Network selector center (loading)
  networkSelectorCenter: {
    justifyContent: 'center',
    minHeight: 64,
  },
  // Network selection modal. Overlay stays transparent — matches
  // DepositCardScreen behaviour where the picker list slides up without
  // dimming the rest of the screen. Tap on the transparent area still
  // closes the modal via the Pressable's onPress.
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
  },
  modalContent: {
    // flex:1 stretches the sheet to the full overlay height so the top
    // of the picker aligns with the top of the screen (matches the
    // Inflow / Top-up screens — single behaviour across Operations).
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
  },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: 'Inter',
    // 16 / 400 — matches sectionLabel / DepositCardScreen.pickerLabel;
    // the modal heading "Current accounts" now reads as a regular label
    // rather than a heavy display title.
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 17,
    color: COLORS.textPrimary,
    marginBottom: 16,
  },
  /* Modal option tile — same proportions as AccountCard on the AllAssets
     screen (66 DP tall, white tile, 1px muted border). Selected state swaps
     the border color to brand-orange. */
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 8,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: {
    borderColor: COLORS.brandOrange,
  },
  modalOptionIcon: {
    // 28 dp matches the picker tile + the equivalent icons on Top-up /
    // Open deposit / Inflow — single icon size across Operations tabs.
    width: 28,
    height: 28,
    marginRight: 10,
  },
  modalOptionTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  modalOptionName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  modalOptionSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  checkmark: {
    fontSize: 20,
    color: COLORS.mint,
    fontWeight: '600',
  },
  /* ── Success modal (post-transfer) ───────────────────────────────────── */
  successOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  successCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    alignItems: 'center',
  },
  successIcon: {
    width: 56,
    height: 56,
    marginBottom: 16,
  },
  successTitle: {
    fontFamily: 'Helvetica Neue',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  successSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 20,
    color: COLORS.blueSteel,
    textAlign: 'center',
    marginTop: 8,
  },
  successAmountRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 24,
    marginBottom: 32,
  },
  successAmount: {
    fontFamily: 'Helvetica Neue',
    fontSize: 32,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 48,
    color: COLORS.textPrimary,
  },
  successCurrency: {
    fontFamily: 'Helvetica Neue',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.textPrimary,
    marginBottom: 6,
  },
  successButton: {
    alignSelf: 'stretch',
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
});

export default WithdrawScreen;
